import { useCallback, useEffect, useState } from "react";
import { useDataSource } from "@firecms/core";

import {
  buildEventsPath,
  eventsCollection,
  type Event,
} from "../collections/events";
import {
  buildAssignedParticipantsCollection,
  buildAssignedParticipantsPath,
} from "../collections/participants";
import {
  buildLevelProgressCollection,
  type LevelProgress,
} from "../collections/levelProgress";
import {
  buildParticipantActionLogsCollection,
  type ActionLogCollectionEntry,
} from "../collections/actionLogs";
import { buildLevelsCollection, buildLevelsPath } from "../collections/levels";
import { buildStoriesCollection, buildStoriesPath } from "../collections/stories";
import { buildUnitCollection, buildUnitsPath } from "../collections/units";
import { participantBaseLabel } from "../firebase/fetchUserDisplayNames";
import type {
  LevelState,
  ParticipantActionLogRow,
  ParticipantSummary,
  UnitProgressSection,
} from "../types/participantProgress";
import type { EventCollectionId } from "../workspace/collectionIds";
import { getEventPath } from "../workspace/collectionIds";
import { getAdminUiLocale } from "../i18n/getAdminUiLocale";
import { resolveLocalizedString } from "../localization";

type CatalogUnit = {
  id: string;
  code: string;
  name: Record<string, string>;
};

type CatalogLevel = {
  id: string;
  code: string;
  order: number;
  modality: string;
  unitId: string;
  storyRef?: { id: string };
};

type CatalogStory = {
  id: string;
  code: string;
  title: Record<string, string>;
};

const defaultState: LevelState = "not_started";

function compareLevels(
  a: { order: number; code: string },
  b: { order: number; code: string }
): number {
  const cmp = a.order - b.order;
  if (cmp !== 0) return cmp;
  return a.code.localeCompare(b.code);
}

function toDate(value: unknown): Date {
  if (value instanceof Date) return value;
  if (
    value != null &&
    typeof value === "object" &&
    "toDate" in value &&
    typeof (value as { toDate: () => Date }).toDate === "function"
  ) {
    return (value as { toDate: () => Date }).toDate();
  }
  return new Date(0);
}

/** Reads string fields from the actionLog payload (levelId/unitId/codes, etc.). */
function payloadString(
  payload: Record<string, unknown>,
  key: string
): string | undefined {
  const value = payload[key];
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

export function useParticipantProgress(eventId: EventCollectionId | null) {
  const dataSource = useDataSource();
  const [participants, setParticipants] = useState<ParticipantSummary[]>([]);
  const [unitSections, setUnitSections] = useState<UnitProgressSection[]>([]);
  const [actionLogs, setActionLogs] = useState<ParticipantActionLogRow[]>([]);
  const [selectedUid, setSelectedUid] = useState<string | null>(null);
  const [loadingParticipants, setLoadingParticipants] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(false);
  const [spaceId, setSpaceId] = useState<string | null>(null);
  const [catalog, setCatalog] = useState<{
    units: CatalogUnit[];
    levels: CatalogLevel[];
    stories: CatalogStory[];
  } | null>(null);

  const loadCatalog = useCallback(async () => {
    if (!eventId) return;

    const [units, levels, stories] = await Promise.all([
      dataSource.fetchCollection({
        path: buildUnitsPath(eventId),
        collection: buildUnitCollection(eventId),
      }),
      dataSource.fetchCollection({
        path: buildLevelsPath(eventId),
        collection: buildLevelsCollection(eventId),
      }),
      dataSource.fetchCollection({
        path: buildStoriesPath(eventId),
        collection: buildStoriesCollection(eventId),
      }),
    ]);

    setCatalog({
      units: units.map((unit) => ({
        id: unit.id,
        code: String(unit.values?.code ?? ""),
        name: (unit.values?.name as Record<string, string>) ?? {},
      })),
      levels: levels.map((level) => {
        const unitRef = level.values?.unitRef as { id?: string } | undefined;
        const storyRef = level.values?.storyRef as { id?: string } | undefined;
        return {
          id: level.id,
          code: String(level.values?.code ?? ""),
          order:
            typeof level.values?.order === "number" ? level.values.order : 0,
          modality: String(level.values?.modality ?? ""),
          unitId: unitRef?.id ?? "",
          storyRef: storyRef?.id ? { id: storyRef.id } : undefined,
        };
      }),
      stories: stories.map((story) => ({
        id: story.id,
        code: String(story.values?.code ?? ""),
        title: (story.values?.title as Record<string, string>) ?? {},
      })),
    });
  }, [dataSource, eventId]);

  const loadParticipants = useCallback(async () => {
    if (!eventId) return;

    setLoadingParticipants(true);
    try {
      const eventEntity = await dataSource.fetchEntity<Event>({
        path: buildEventsPath({ accountId: eventId.accountId }),
        collection: eventsCollection,
        entityId: eventId.eventId,
      });
      const spaceRef = eventEntity?.values?.space as { id?: string } | undefined;
      setSpaceId(spaceRef?.id || null);
      await loadCatalog();

      const assigned = await dataSource.fetchCollection({
        path: buildAssignedParticipantsPath(eventId),
        collection: buildAssignedParticipantsCollection(eventId),
      });

      const loaded: ParticipantSummary[] = assigned.map((participant) => ({
        uid: participant.id,
        label: participantBaseLabel(
          participant.id,
          participant.values?.participantNumber
        ),
      }));

      loaded.sort((a, b) => a.label.localeCompare(b.label));
      setParticipants(loaded);
    } finally {
      setLoadingParticipants(false);
    }
  }, [dataSource, eventId, loadCatalog]);

  const loadParticipantData = useCallback(
    async (participantId: string) => {
      if (!eventId || !catalog) return;

      setLoadingProgress(true);
      try {
        const locale = getAdminUiLocale();
        const participantBase = `${getEventPath(eventId)}/participant/${participantId}`;

        const [progressEntities, logEntities] = await Promise.all([
          dataSource.fetchCollection<LevelProgress>({
            path: `${participantBase}/levelProgress`,
            collection: buildLevelProgressCollection(eventId),
          }),
          dataSource.fetchCollection<ActionLogCollectionEntry>({
            path: `${participantBase}/actionLogs`,
            collection: buildParticipantActionLogsCollection(eventId),
          }),
        ]);

        const progressByLevelId = new Map<string, LevelState>();
        for (const progress of progressEntities) {
          const state = progress.values?.state as LevelState | undefined;
          progressByLevelId.set(progress.id, state ?? defaultState);
        }

        const levelById = new Map(
          catalog.levels.map((level) => [level.id, level])
        );
        const storyById = new Map(
          catalog.stories.map((story) => [story.id, story])
        );

        const sections: UnitProgressSection[] = catalog.units
          .slice()
          .sort((a, b) => a.code.localeCompare(b.code))
          .map((unit) => {
            const unitLevels = catalog.levels
              .filter((level) => level.unitId === unit.id)
              .sort(compareLevels);

            const levels = unitLevels.map((level, index) => {
              const story = level.storyRef?.id
                ? storyById.get(level.storyRef.id)
                : undefined;

              return {
                levelKey: String(level.order > 0 ? level.order : index + 1),
                order: level.order > 0 ? level.order : index + 1,
                levelId: level.id,
                levelCode: level.code,
                modality: level.modality,
                storyTitle: story
                  ? resolveLocalizedString(story.title, locale)
                  : undefined,
                state: progressByLevelId.get(level.id) ?? defaultState,
              };
            });

            return {
              unitId: unit.id,
              unitCode: unit.code,
              unitName: resolveLocalizedString(unit.name, locale),
              levels,
            };
          });

        setUnitSections(sections);

        const logs: ParticipantActionLogRow[] = logEntities
          .map((log) => {
            const data = log.values;
            const payload = (data?.payload ?? {}) as Record<string, unknown>;
            const levelCodeFromPayload = payloadString(payload, "levelCode");
            const unitCodeFromPayload = payloadString(payload, "unitCode");
            const levelId = payloadString(payload, "levelId");
            const unitId = payloadString(payload, "unitId");

            return {
              id: log.id,
              actionAt: toDate(data?.actionAt),
              actionType: String(data?.actionType ?? ""),
              levelCode:
                levelCodeFromPayload ||
                (levelId ? levelById.get(levelId)?.code : undefined),
              unitCode:
                unitCodeFromPayload ||
                (unitId
                  ? catalog.units.find((u) => u.id === unitId)?.code
                  : undefined),
            };
          })
          .sort((a, b) => b.actionAt.getTime() - a.actionAt.getTime());

        setActionLogs(logs);
      } finally {
        setLoadingProgress(false);
      }
    },
    [catalog, dataSource, eventId]
  );

  useEffect(() => {
    void loadParticipants();
  }, [loadParticipants]);

  useEffect(() => {
    if (!selectedUid) {
      setUnitSections([]);
      setActionLogs([]);
      return;
    }
    void loadParticipantData(selectedUid);
  }, [loadParticipantData, selectedUid]);

  return {
    participants,
    unitSections,
    actionLogs,
    selectedUid,
    setSelectedUid,
    loading: loadingParticipants || loadingProgress,
    loadingParticipants,
    loadingProgress,
    refresh: loadParticipants,
    spaceId,
  };
}
